import { useReducer } from 'react';

import { FieldWithParent } from 'src/app/graph/explorerPage/search/searchSchema/searchSchema';
import { assertUnreachable } from 'src/lib/assertUnreachable';

type SearchingState = {
  state: 'searching';
  visible: true;
  searchQuery?: string;
  highlightedIndex?: number | null;
};
type ChoosingPathState = {
  state: 'choosing path';
  visible: true;
  fieldInfo: FieldWithParent;
  previousState: SearchingState;
  highlightedIndex?: number | null;
};
export type SearchState =
  | { state: 'unopened'; visible: false }
  | SearchingState
  | ChoosingPathState
  | {
      state: 'cancelled';
      visible: false;
      previousState: SearchState;
      timestamp: number;
    }
  | {
      state: 'complete';
      visible: false;
      previousState: SearchState;
      timestamp: number;
    };

export type SearchStateAction =
  | { type: 'open' }
  | { type: 'toggle' }
  | { type: 'set search query'; searchQuery: string }
  | {
      type: 'return to search';
      searchQuery?: string;
      highlightedIndex?: number | null;
    }
  | {
      type: 'set highlightedIndex';
      highlightedIndex?: number | null;
    }
  | {
      type: 'select field';
      selectedItem: FieldWithParent;
    }
  | { type: 'select path' }
  | { type: 'cancel' };

function searchStateReducer(
  currentState: SearchState,
  action: SearchStateAction,
): SearchState {
  const RESTORE_SEARCH_STATE_TIMEOUT = 90 * 1000;
  const now = Date.now();
  switch (action.type) {
    case 'open':
      if (
        (currentState.state === 'cancelled' ||
          currentState.state === 'complete') &&
        now - currentState.timestamp < RESTORE_SEARCH_STATE_TIMEOUT
      ) {
        return currentState.previousState;
      }
      return { state: 'searching', visible: true };
    case 'toggle':
      if (currentState.visible) {
        return {
          state: 'cancelled',
          visible: false,
          previousState: currentState,
          timestamp: now,
        };
      }
      if (
        (currentState.state === 'cancelled' ||
          currentState.state === 'complete') &&
        now - currentState.timestamp < RESTORE_SEARCH_STATE_TIMEOUT
      ) {
        return currentState.previousState;
      }
      return { state: 'searching', visible: true };
    case 'set search query':
      return {
        state: 'searching',
        visible: true,
        searchQuery: action.searchQuery,
      };
    case 'return to search':
      return {
        state: 'searching',
        visible: true,
        searchQuery: action.searchQuery,
        highlightedIndex: action.highlightedIndex,
      };
    case 'cancel':
      return {
        state: 'cancelled',
        visible: false,
        previousState: currentState,
        timestamp: Date.now(),
      };
    case 'select field':
      if (currentState.state !== 'searching') {
        throw new Error(
          `Cannot perform action "${action.type}" from state "${currentState.state}"`,
        );
      }
      return {
        state: 'choosing path',
        visible: true,
        fieldInfo: action.selectedItem,
        previousState: currentState,
      };
    case 'set highlightedIndex': {
      if (
        currentState.state !== 'searching' &&
        currentState.state !== 'choosing path'
      ) {
        throw new Error(
          `Cannot perform action "${action.type}" from state "${currentState.state}"`,
        );
      }
      return {
        ...currentState,
        highlightedIndex: action.highlightedIndex,
      };
    }
    case 'select path':
      return {
        state: 'complete',
        visible: false,
        previousState: currentState,
        timestamp: now,
      };
    default:
      throw assertUnreachable(action);
  }
}

export const useSearchState = () => {
  const [searchState, dispatchSearchAction] = useReducer(searchStateReducer, {
    state: 'unopened',
    visible: false,
  });
  return {
    searchState,
    dispatchSearchAction,
  };
};
